import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import {
  ASSESSMENT_RESULT_OPTIONS,
  EMPLOYMENT_TYPES,
  FORM_REFS
} from '../../constants/instructorAuthorization';
import AuthorizationAttachmentsSection from '../authorization/AuthorizationAttachmentsSection';
import RowAttachmentsCell from '../authorization/RowAttachmentsCell';
import { rowPendingKey } from '../../utils/authorizationAttachments';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';

const emptyExperienceRow = () => ({
  natureOfWork: '',
  organisation: '',
  fromDate: '',
  toDate: '',
  attachments: []
});

const emptyTrainingRow = () => ({
  trainingType: '',
  duration: '',
  conductedBy: '',
  startDate: '',
  validity: '',
  attachments: []
});

const emptyCompanyPolicyAssessment = () => ({
  moeResult: '',
  auditResult: '',
  remarks: '',
  coordinatorName: '',
  coordinatorDate: ''
});

export const emptyGetsCm002bForm = (overrides = {}) => ({
  applicantName: '',
  applicationType: 'ISSUE',
  employeeType: 'PERMANENT',
  employeeId: '',
  trainTheTrainer: false,
  caNo: '',
  caExpiry: '',
  requiredScopeOfApproval: '',
  qualificationDetails: '',
  experienceDetails: [emptyExperienceRow()],
  trainingDetails: [emptyTrainingRow()],
  applicantDeclarationDate: '',
  applicantDeclarationPlace: '',
  companyPolicyAssessment: emptyCompanyPolicyAssessment(),
  ...overrides
});

function GetsCm002bApplicationForm({
  value,
  onChange,
  readOnly = false,
  showHeader = true,
  onDownloadPdf,
  attachments,
  pendingFiles,
  attachmentsSaving = false,
  onAddAttachmentFiles,
  onRemovePendingAttachment,
  onViewAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
  sectionAttachments,
  pendingSectionFiles,
  onAddSectionFiles,
  onRemovePendingSectionFile,
  onDeleteSectionAttachment,
  pendingRowFiles,
  onAddRowFiles,
  onRemovePendingRowFile,
  onDeleteRowAttachment,
  onRemoveRowIndex,
  attachmentsReadOnly
}) {
  const form = value || emptyGetsCm002bForm();
  const set = (patch) => onChange({ ...form, ...patch });
  const attsReadOnly =
    typeof attachmentsReadOnly === 'boolean' ? attachmentsReadOnly : readOnly;

  const experience =
    Array.isArray(form.experienceDetails) && form.experienceDetails.length
      ? form.experienceDetails
      : [emptyExperienceRow()];
  const trainings =
    Array.isArray(form.trainingDetails) && form.trainingDetails.length
      ? form.trainingDetails
      : [emptyTrainingRow()];
  const cpa = {
    ...emptyCompanyPolicyAssessment(),
    ...(form.companyPolicyAssessment || {})
  };

  const updateExperience = (index, patch) => {
    const next = experience.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ experienceDetails: next });
  };
  const updateTraining = (index, patch) => {
    const next = trainings.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ trainingDetails: next });
  };
  const removeExperienceRow = (index) => {
    set({ experienceDetails: experience.filter((_, i) => i !== index) });
    onRemoveRowIndex?.('experience', index);
  };
  const removeTrainingRow = (index) => {
    set({ trainingDetails: trainings.filter((_, i) => i !== index) });
    onRemoveRowIndex?.('training', index);
  };
  const setCpa = (patch) => set({ companyPolicyAssessment: { ...cpa, ...patch } });

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');
  const sectionAtts = sectionAttachments || {};
  const pendingSections = pendingSectionFiles || {};
  const pendingRows = pendingRowFiles || {};
  const renderSectionAttachments = (section) => (
    <Box sx={{ mt: 1.5 }}>
      <AuthorizationAttachmentsSection
        title="Attachments"
        attachments={sectionAtts[section] || []}
        pendingFiles={pendingSections[section] || []}
        readOnly={attsReadOnly}
        saving={attachmentsSaving}
        onAddFiles={
          onAddSectionFiles ? (files) => onAddSectionFiles(section, files) : undefined
        }
        onRemovePending={
          onRemovePendingSectionFile
            ? (index) => onRemovePendingSectionFile(section, index)
            : undefined
        }
        onView={onViewAttachment}
        onDownload={onDownloadAttachment}
        onDelete={
          onDeleteSectionAttachment
            ? (att) => onDeleteSectionAttachment(section, att)
            : undefined
        }
      />
    </Box>
  );
  const renderRowAttachments = (section, row, index) => (
    <RowAttachmentsCell
      attachments={row.attachments || []}
      pendingFiles={pendingRows[rowPendingKey(section, index)] || []}
      readOnly={attsReadOnly}
      saving={attachmentsSaving}
      onAddFiles={
        onAddRowFiles ? (files) => onAddRowFiles(section, index, files, row) : undefined
      }
      onRemovePending={
        onRemovePendingRowFile
          ? (fileIndex) => onRemovePendingRowFile(section, index, fileIndex)
          : undefined
      }
      onView={onViewAttachment}
      onDownload={onDownloadAttachment}
      onDelete={
        onDeleteRowAttachment
          ? (att) => onDeleteRowAttachment(section, row, att)
          : undefined
      }
    />
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderColor: 'text.primary',
        borderWidth: 2,
        borderStyle: 'double',
        bgcolor: 'background.paper'
      }}
    >
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <GetsBrandHeader
            title="Application for Issue / Extension / Renewal of Instructor Authorization"
            subtitle={`Form: ${FORM_REFS.application} | Issue: 01 Rev 00 Dtd Jul 2026`}
          />
          {onDownloadPdf && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: -1, mb: 1 }}>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
                Download {FORM_REFS.application} PDF
              </Button>
            </Stack>
          )}
        </Box>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            required
            label="Name of the Applicant"
            value={form.applicantName || ''}
            onChange={(e) => set({ applicantName: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select={!readOnly}
            fullWidth
            size="small"
            label="Employee Type"
            value={form.employeeType || ''}
            onChange={(e) => set({ employeeType: e.target.value })}
            {...fieldProps}
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormControl component="fieldset" disabled={readOnly} sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ typography: 'body2', mb: 0.5 }}>
              Application for
            </FormLabel>
            <RadioGroup
              row
              value={form.applicationType || 'ISSUE'}
              onChange={(e) => set({ applicationType: e.target.value })}
            >
              <FormControlLabel value="ISSUE" control={<Radio size="small" />} label="Issue" />
              <FormControlLabel value="EXTENSION" control={<Radio size="small" />} label="Extension" />
              <FormControlLabel value="RENEWAL" control={<Radio size="small" />} label="Renewal" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Employee ID"
            value={form.employeeId || ''}
            onChange={(e) => set({ employeeId: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl component="fieldset" disabled={readOnly} sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ typography: 'body2', mb: 0.5 }}>
              Train the Trainer
            </FormLabel>
            <RadioGroup
              row
              value={form.trainTheTrainer ? 'YES' : 'NO'}
              onChange={(e) => set({ trainTheTrainer: e.target.value === 'YES' })}
            >
              <FormControlLabel value="YES" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="NO" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="CA No."
            value={form.caNo || ''}
            onChange={(e) => set({ caNo: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="CA Expiry"
            InputLabelProps={{ shrink: true }}
            value={dateVal(form.caExpiry)}
            onChange={(e) => set({ caExpiry: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Required Scope of Approval on Type of Training"
            value={form.requiredScopeOfApproval || ''}
            onChange={(e) => set({ requiredScopeOfApproval: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase' }}>
          Qualification Details
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          *Attach Course completion Certificate as applicable.
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={4}
          label="Qualification Details"
          value={form.qualificationDetails || ''}
          onChange={(e) => set({ qualificationDetails: e.target.value })}
          {...fieldProps}
        />
        {renderSectionAttachments('qualification')}
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
            Recent Experience (2 Years) Details
          </Typography>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => set({ experienceDetails: [...experience, emptyExperienceRow()] })}
            >
              Add row
            </Button>
          )}
        </Stack>
        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48}>Sl.No</TableCell>
                <TableCell>Nature of Work</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Attachments</TableCell>
                {!readOnly && <TableCell width={48} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {experience.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.natureOfWork || ''}
                      onChange={(e) => updateExperience(index, { natureOfWork: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.organisation || ''}
                      onChange={(e) => updateExperience(index, { organisation: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={dateVal(row.fromDate)}
                      onChange={(e) => updateExperience(index, { fromDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={dateVal(row.toDate)}
                      onChange={(e) => updateExperience(index, { toDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>{renderRowAttachments('experience', row, index)}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={experience.length <= 1}
                        onClick={() => removeExperienceRow(index)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
              Training Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enclose training certificates — General, HF, SMS, CAR & Aircraft-Specific
            </Typography>
          </Box>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => set({ trainingDetails: [...trainings, emptyTrainingRow()] })}
            >
              Add row
            </Button>
          )}
        </Stack>
        <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type of Training Undergone</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Conducted by</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Attachments</TableCell>
                {!readOnly && <TableCell width={48} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {trainings.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.trainingType || ''}
                      onChange={(e) => updateTraining(index, { trainingType: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.duration || ''}
                      onChange={(e) => updateTraining(index, { duration: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      value={row.conductedBy || ''}
                      onChange={(e) => updateTraining(index, { conductedBy: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={dateVal(row.startDate)}
                      onChange={(e) => updateTraining(index, { startDate: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      value={dateVal(row.validity)}
                      onChange={(e) => updateTraining(index, { validity: e.target.value })}
                      {...fieldProps}
                    />
                  </TableCell>
                  <TableCell>{renderRowAttachments('training', row, index)}
                  </TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={trainings.length <= 1}
                        onClick={() => removeTrainingRow(index)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
      </Box>

      <Box sx={{ mb: 2 }}>
        <AuthorizationAttachmentsSection
          title="Other attachments"
          attachments={
            Array.isArray(attachments)
              ? attachments
              : Array.isArray(form.applicationAttachments)
                ? form.applicationAttachments
                : []
          }
          pendingFiles={pendingFiles}
          readOnly={attsReadOnly}
          saving={attachmentsSaving}
          onAddFiles={onAddAttachmentFiles}
          onRemovePending={onRemovePendingAttachment}
          onView={onViewAttachment}
          onDownload={onDownloadAttachment}
          onDelete={onDeleteAttachment}
        />
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
          I hereby certify that the details given above are accurate, complete and current and I am
          conversant with organization policy and procedures appropriate to the holder of a company
          approval.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Sign. of Applicant"
              value={form.applicantName || ''}
              helperText="Name as signature acknowledgement"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(form.applicantDeclarationDate)}
              onChange={(e) => set({ applicantDeclarationDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Place"
              value={form.applicantDeclarationPlace || ''}
              onChange={(e) => set({ applicantDeclarationPlace: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Company Policy &amp; Procedure Assessment
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          1. The Applicant has been assessed for the applicable MOE &amp; related procedures and found
          to be:
        </Typography>
        <TextField
          select={!readOnly}
          fullWidth
          size="small"
          label="MOE assessment"
          value={cpa.moeResult || ''}
          onChange={(e) => setCpa({ moeResult: e.target.value })}
          sx={{ mb: 2 }}
          {...fieldProps}
        >
          <MenuItem value="">—</MenuItem>
          {ASSESSMENT_RESULT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" sx={{ mb: 1 }}>
          2. Checked for any audit observations with respect to the skill levels / job deficiencies
          requiring additional training and found to be:
        </Typography>
        <TextField
          select={!readOnly}
          fullWidth
          size="small"
          label="Audit observations assessment"
          value={cpa.auditResult || ''}
          onChange={(e) => setCpa({ auditResult: e.target.value })}
          sx={{ mb: 2 }}
          {...fieldProps}
        >
          <MenuItem value="">—</MenuItem>
          {ASSESSMENT_RESULT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Remarks (Related to the individual's past performance as an approval holder)"
          value={cpa.remarks || ''}
          onChange={(e) => setCpa({ remarks: e.target.value })}
          sx={{ mb: 2 }}
          {...fieldProps}
        />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Training Co-ordinator name"
              value={cpa.coordinatorName || ''}
              onChange={(e) => setCpa({ coordinatorName: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Training Co-ordinator date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cpa.coordinatorDate)}
              onChange={(e) => setCpa({ coordinatorDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default GetsCm002bApplicationForm;
